import React from 'react';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FeaturedAnalysis } from '../../hooks/useFeaturedAnalyses';

interface DeepAnalysisCardProps {
  analysis: FeaturedAnalysis;
  onAction: (type: string) => void;
  showAuthModal?: () => void;
}

const DeepAnalysisCard: React.FC<DeepAnalysisCardProps> = ({ analysis, onAction, showAuthModal }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(user);

  const handleAuthAction = (actionType: string) => {
    if (isLoggedIn) {
      onAction(actionType);
    } else if (showAuthModal) {
      showAuthModal();
    }
  };

  // 生成链接 URL，官方深度分析使用 official-analysis 路由
  const linkUrl = analysis.slug ? `/official-analysis/${analysis.slug}` : `/official-analysis/${analysis.id}`;

  return (
    <a
      href={linkUrl}
      className="bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 h-full flex flex-col group cursor-pointer block overflow-hidden"
    >
      {/* 顶部渐变条 */}
      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>
      
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 text-xs font-semibold rounded-full flex-shrink-0">
            {analysis.category}
          </span>
          <span className="text-xs text-gray-500 ml-2 flex-shrink-0 bg-gray-100 px-2 py-1 rounded">
            {new Date(analysis.created_at).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        
        <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200 leading-tight">
          {analysis.title}
        </h3>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-grow">
          {analysis.summary.slice(0, 120)}...
        </p>
        
        {analysis.preview_content && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-4 border-l-4 border-blue-400">
            <p className="text-xs text-gray-700 line-clamp-2">
              <span className="font-semibold text-blue-800">AI分析预览：</span>
              {analysis.preview_content.slice(0, 80)}...
            </p>
          </div>
        )}
        
        <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-3">
            <span className="flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
              {Math.floor(Math.random() * 1000) + 100}
            </span>
            <span className="flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
              </svg>
              {Math.floor(Math.random() * 100) + 10}
            </span>
          </div>
          <span className="px-2 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full text-xs font-semibold">
            {Math.floor(Math.random() * 5) + 3}分钟阅读
          </span>
        </div>
      </div>
    </a>
  );
};

export default DeepAnalysisCard;
